import React from 'react';
import { Field, useFormState } from 'react-final-form';
import { useIntl } from 'react-intl';

import { RFFFieldErrorWrapper } from 'common/components';
import InputNumber, { InputNumberProps } from 'common/components/InputNumber';
import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { useLocaleUtils } from 'common/hooks';
import { useCountryConfigContext } from 'modules/country/CountryConfigContext';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';

import { useRowOverrideContext } from './WithOverrideToggle';
import { BaseField } from './types';
import { useOfferOverridesContext } from '../../../context/OfferOverridesContext';
import { FormRowRenderer } from '../FormRowRenderer';
import { DisplayText, PreviewRowContainer, StyledInputContainer } from '../OfferDetailsFormFields.styles';
import { FormValues } from '../types';
import { getCurrencyMaxDecimals } from '../utils';

type InputMonetaryProps = Omit<InputNumberProps, 'decimals'> & {
  currency: Currency;
};

const InputMonetary = ({ currency, suffixText, ...rest }: InputMonetaryProps) => {
  const maxCurrencyDecimals = getCurrencyMaxDecimals(currency);

  return <InputNumber {...rest} suffixText={suffixText ? suffixText : currency} decimals={maxCurrencyDecimals} />;
};

export const Price = ({ sidePanelMode, offer }: BaseField) => {
  const menuPermissions = useOfferingMenuPermissionsContext();
  const intl = useIntl();
  const { values } = useFormState<FormValues>();
  const { getCurrencySymbol, getLocalizedUnit } = useLocaleUtils();
  const { currency } = useCountryConfigContext();
  const { isOverrideToggleOn, isRowGroupEnabledForOverride } = useRowOverrideContext();
  const { maybeOverriddenOffer, getOfferAttrHasOverride } = useOfferOverridesContext();

  const isPriceOverridden = getOfferAttrHasOverride('price');

  const fieldNameToUse: keyof FormValues = isOverrideToggleOn ? 'priceOverride' : 'price';
  const offerToUse = isOverrideToggleOn ? maybeOverriddenOffer : offer;
  const discounted = !!offerToUse?.discounted;
  // TODO: check if makes sense to default to 0
  const offerPriceToUse = discounted ? offerToUse?.discounted?.original_price ?? 0 : offerToUse?.price ?? 0;

  const previewComponent = (
    <PreviewRowContainer>
      <DisplayText>
        {!!maybeOverriddenOffer ? (
          <>
            <MonetaryAmount
              amount={typeof offerPriceToUse === 'number' ? offerPriceToUse : parseFloat(offerPriceToUse)}
              currency={currency}
            />
            {!!offerToUse?.sell_by_weight_config && `/${getLocalizedUnit('kilogram')}`}
          </>
        ) : (
          '-'
        )}
      </DisplayText>
    </PreviewRowContainer>
  );

  const editComponent = (
    <Field name={fieldNameToUse}>
      {field => (
        <RFFFieldErrorWrapper field={field}>
          {input => (
            <StyledInputContainer>
              {values.weightType === 'pre-packaged' && <InputMonetary {...input} currency={currency} />}
              {values.weightType === 'weighted-items' && (
                <InputMonetary
                  {...input}
                  suffixText={`${getCurrencySymbol(currency)}/${getLocalizedUnit('kilogram')}`}
                  currency={currency}
                />
              )}
            </StyledInputContainer>
          )}
        </RFFFieldErrorWrapper>
      )}
    </Field>
  );

  const fieldLabelKey =
    !discounted || sidePanelMode === 'create' || sidePanelMode === 'edit'
      ? 'menu-item.price'
      : 'special.original-price';

  return (
    <FormRowRenderer
      hasContentEditPermission={menuPermissions.contentEdit}
      label={intl.formatMessage({ id: fieldLabelKey })}
      shouldDisplayCreateTag={!values[fieldNameToUse]}
      editComponent={editComponent}
      previewComponent={previewComponent}
      offer={offer}
      isOverridden={isPriceOverridden}
      canBeOverridden={isRowGroupEnabledForOverride}
      required
      sidePanelMode={sidePanelMode}
    />
  );
};
